import { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';

// Module-level cache so we only hit the DB once per session.
let _cache = null;
let _inflight = null;
const _subs = new Set();

function mapRecord(r) {
  return {
    // legacy id == sku (components key on this)
    id: r.sku,
    sku: r.sku,
    slug: r.slug,
    productCode: r.product_code,
    name: r.name,
    brand: r.brand,
    badge: r.badge,
    tagline: r.tagline,
    brand_subtext: r.brand_subtext,
    short_desc: r.short_desc,
    unique_point: r.unique_point,
    description: r.description,
    highlighted: !!r.highlighted,
    status: r.status || 'active',
    price: r.price ?? null,
    original_price: r.original_price ?? null,
    campaign_label: r.campaign_label,
    image: r.image,
    image_scale: r.image_scale || '',
    lifestyle_image: r.lifestyle_image,
    gallery_images: r.gallery_images || [],
    tags: r.tags || [],
    features: r.features || [],
    key_features: r.key_features || [],
    specs: r.specs || [],
    detailPath: r.detail_path,
    detail_path: r.detail_path,
    meter_note_title: r.meter_note_title,
    meter_note_body: r.meter_note_body,
    noindex: !!r.noindex,
    sort_order: r.sort_order ?? 0,
  };
}

function _load() {
  return base44.entities.Charger.list('sort_order', 50)
    .then((rows) => {
      const mapped = (rows || [])
        .filter((r) => r.status !== 'hidden')
        .map(mapRecord)
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
      _cache = mapped;
      _inflight = null;
      _subs.forEach((fn) => fn(mapped));
      return mapped;
    })
    .catch(() => { _inflight = null; return []; });
}

async function fetchCatalog() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = _load();
  return _inflight;
}

/**
 * Force a fresh fetch from the DB and notify every mounted consumer.
 * Call this after admin edits so changes propagate site-wide without a reload.
 */
export async function refreshCatalog() {
  if (_inflight) return _inflight;
  _cache = null;
  _inflight = _load();
  return _inflight;
}

/**
 * useChargerCatalog
 * Returns DB-backed charger catalog plus derived price maps that mirror the
 * legacy chargerPriceRegistry shape, so components can swap in with minimal change.
 *
 * { chargers, loading, byId, basePrices, originalPrices, getCharger }
 */
export function useChargerCatalog({ enabled = true } = {}) {
  const [chargers, setChargers] = useState(_cache || []);
  const [loading, setLoading] = useState(!_cache);

  useEffect(() => {
    if (!enabled) return;
    let active = true;
    const onUpdate = (data) => { if (active) { setChargers(data); setLoading(false); } };
    _subs.add(onUpdate);
    if (_cache) {
      setChargers(_cache);
      setLoading(false);
    } else {
      fetchCatalog().then((data) => { if (active) { setChargers(data); setLoading(false); } });
    }
    return () => { active = false; _subs.delete(onUpdate); };
  }, [enabled]);

  const byId = {};
  const basePrices = {};
  const originalPrices = {};
  for (const c of chargers) {
    byId[c.id] = c;
    if (c.price != null) basePrices[c.id] = c.price;
    originalPrices[c.id] = c.original_price ?? c.price ?? null;
  }

  const getCharger = (id) => byId[id] || null;
  const getBySku = (sku) => byId[sku] || null;

  return { chargers, loading, byId, basePrices, originalPrices, getCharger, getBySku, refreshCatalog };
}

export default useChargerCatalog;